import _ from 'lodash';
import { createStorageInstance, destroyDatabase } from 'zapier-async-storage';

import { createErrorType } from 'app/common/error';

const defaultDBName = 'zapier-entity-cache';

export const InvalidCacheError = createErrorType('InvalidCacheError', {
  summary() {
    return 'Entity cache expects all entities to have an `id` property.';
  },
});

/**
 * The key used to inform our code that cache cleanup has occured. If this
 * entry is found in the backbone cache, it will skip performing the cleanup
 * code.
 */
const backboneCacheDbName = 'backbonecache';
const backboneCacheCleanupKey = 'cache-clean-up';
const backboneCacheCleanupValue = '3';

/**
 * Contains the endpoints which we consider to still be valid usage of the
 * backbone cache. These strings are checked using String.includes(), they
 * are not exact matched.
 */
const validBackboneData = ['/api/developer/v1'];

/**
 * Contains the tables which we should no longer store data for. Placing an
 * entry in this list will force the table to be cleared when the backbone data
 * is removed.
 */
const deprecatedDatabases = ['localforage', 'service'];

const getDatabaseTable = (dbName, storeName) => {
  return createStorageInstance({
    dbName,
    storeName: storeName ? storeName : 'keyvaluepairs',
    version: backboneCacheCleanupValue,
  });
};

export const clearTables = (databaseId, tableIdList) => {
  const promises = tableIdList.map(tableId =>
    getDatabaseTable(databaseId, tableId).clear()
  );

  return Promise.all(promises);
};

let hasInitialized = false;

export const clearLegacyData = () => {
  const deprecatedDbPromises = deprecatedDatabases.map(entry =>
    destroyDatabase(entry)
  );

  const backboneCleanupPromises = [];

  const backboneTable = getDatabaseTable(backboneCacheDbName);

  return backboneTable
    .getItem(backboneCacheCleanupKey)
    .then(cleanupValue => {
      // If the value is `undefined` then it is a clear cache storage. We do
      // not need to perform clean up. This will prevent incognito windows from
      // attempting to clear cache.
      if (
        _.isUndefined(cleanupValue) ||
        cleanupValue === backboneCacheCleanupValue
      ) {
        return Promise.resolve();
      }

      return backboneTable
        .iterate((value, key) => {
          if (
            !_.some(validBackboneData, endpoint => _.includes(key, endpoint))
          ) {
            backboneCleanupPromises.push(backboneTable.removeItem(key));
          }
        })
        .then(() =>
          Promise.all([...deprecatedDbPromises, ...backboneCleanupPromises])
        )
        .then(() =>
          backboneTable.setItem(
            backboneCacheCleanupKey,
            backboneCacheCleanupValue
          )
        );
    })
    .catch(error => {
      // In Firefox incognito mode, connections to databases will result in
      // an InvalidStateError. If this occurs, then do not attempt to clear
      // the cache forcefully because there is no cache in incognito mode.
      // This will prevent errors like this being logged in Sentry:
      // http://bit.ly/2hraqem
      if (error && error.name === 'InvalidStateError') {
        return Promise.resolve();
      }

      console.error(
        'An error occured while attempting to clear cache storage. Proceeding with forcefully clearing cache. ',
        error
      );

      // Kill the connection to the backbone table
      backboneTable.close();

      // If we encounter an error during clean up, then nuke all the databases
      // to be safe. This might occur as a result of mis-matching versions.
      return Promise.all([
        ...deprecatedDbPromises,
        destroyDatabase(backboneCacheDbName),
      ])
        .then(() => console.warn('Successfully removed all storage databases.'))
        .catch(err =>
          console.error('Unable to remove storage databases.', err)
        );
    });
};

export const clearStaleCacheData = () => {
  const next = hasInitialized ? Promise.resolve(true) : clearLegacyData();

  hasInitialized = true;

  return next;
};

export const createCache = (
  entityType = defaultDBName,
  tableName,
  invalidApis = []
) => {
  const table = createStorageInstance({
    name: entityType,
    storeName: tableName,
  });

  const createEntityCache = () => {
    return {
      getItem(id) {
        return table.getItem(id);
      },

      setItem(id, data) {
        return table.setItem(id, data);
      },

      // When provided a collection of data, it will overwrite the table with
      // the provided data
      setCollection(collection) {
        const saveCollectionPromises = collection.map(item => {
          const id = _.get(item, 'id').toString();

          if (!id) {
            throw new InvalidCacheError();
          }

          return table.setItem(id, item);
        });

        // Clear the previous value of the table so that we don't have old data
        // hanging around
        return table.clear().then(Promise.all(saveCollectionPromises));
      },

      getCollection() {
        const collection = [];

        return table
          .iterate(value => {
            collection.push(value);
          })
          .then(() => collection);
      },
    };
  };

  return clearTables(entityType, invalidApis).then(() =>
    Promise.resolve(createEntityCache())
  );
};

export default createCache;
